
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { PlanSelector } from "@/components/settings/PlanSelector";
import { useFeatureAccess } from "@/hooks/use-feature-access";
import BrandingTab from "@/components/settings/BrandingTab";

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState(user?.name || "");
  const [agencyName, setAgencyName] = useState(user?.agency_name || "");
  const tab = new URLSearchParams(location.search).get("tab") || "profile";
  const { userPlan } = useFeatureAccess();

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setAgencyName(user.agency_name || "");
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    try {
      const { error } = await supabase
        .from("users")
        .update({ name: name, agency_name: agencyName })
        .eq("id", user?.id);

      if (error) {
        throw error;
      }

      toast.success("Perfil atualizado com sucesso!");
    } catch (error: any) {
      toast.error("Erro ao atualizar o perfil: " + error.message);
    }
  };

  const handleChangePassword = () => {
    navigate("/update-password");
  };

  return (
    <div className="container py-6 max-w-5xl">
      <h1 className="text-3xl font-semibold mb-6">Configurações</h1>

      <Tabs defaultValue={tab} className="space-y-6">
        <TabsList className="border mb-4">
          <TabsTrigger value="profile" className="text-sm">Perfil</TabsTrigger>
          <TabsTrigger value="account" className="text-sm">Conta</TabsTrigger>
          <TabsTrigger value="branding" className="text-sm">Marca</TabsTrigger>
          <TabsTrigger value="plan" className="text-sm">Plano</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informações do Perfil</CardTitle>
                <CardDescription>
                  Atualize suas informações de perfil.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="agencyName">Nome da Agência</Label>
                  <Input
                    type="text"
                    id="agencyName"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                  />
                </div>
                <Button onClick={handleUpdateProfile}>Atualizar Perfil</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="account">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informações da Conta</CardTitle>
                <CardDescription>
                  Gerencie as configurações da sua conta.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback>{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium leading-none">{user?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                </div>
                <Button onClick={handleChangePassword}>Mudar Senha</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="branding">
          <BrandingTab />
        </TabsContent>

        <TabsContent value="plan">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Plano da Sua Agência</CardTitle>
                <CardDescription>
                  Gerencie o plano de assinatura da sua agência
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PlanSelector />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
